import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HiOutlineBuildingOffice2, HiOutlineEnvelope, HiOutlinePhone, HiOutlineMapPin, HiOutlineIdentification, HiPencilSquare, HiCheck, HiXMark, HiOutlineUser, HiCamera, HiOutlineBriefcase } from 'react-icons/hi2';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { authService, stripTenantModuleHints } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import './Company.css';

const mapContainerStyle = {
    width: '100%',
    height: '300px',
    borderRadius: '8px',
    marginTop: '0.5rem',
    border: '1px solid var(--border-primary)'
};

const defaultCenter = {
    lat: 22.3072,
    lng: 73.1812 // Vadodara
};

const libraries = ['places'];

const Company = () => {
    const { hasPermission } = usePermissions();

    // Permission check
    const canManageTenant = hasPermission('Tenant.Manage');

    const fileInputRef = useRef(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [showCustomIndustry, setShowCustomIndustry] = useState(false);
    const [geoError, setGeoError] = useState('');

    // Track if user is manually typing the address to trigger geocoding
    const isTypingLocation = useRef(false);



    const INDUSTRY_OPTIONS = [
        "Pharma",
        "Chemical",
        "Food",
        "Manufacturing",
        "Automotive",
        "Textile",
        "Electronics",
        "Construction",
        "Energy",
        "Logistics"
    ];

    // Load Google Maps API (REPLACE 'YOUR_API_KEY_HERE' WITH ACTUAL KEY)
    const { isLoaded: isMapLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "", // ⚠️ INSERT YOUR GOOGLE MAPS API KEY HERE
        libraries
    });

    useEffect(() => {
        if (canManageTenant) {
            loadCompanyInfo();
        } else {
            setError('You do not have permission to manage tenant information');
            setLoading(false);
        }
    }, [canManageTenant]);

    // Geocoding Effect: Update Lat/Lng when Address changes
    useEffect(() => {
        // Only active when editing
        if (!isEditing || !isMapLoaded || !isTypingLocation.current || !editForm.address || editForm.address.length < 4) {
            return;
        }

        const delayDebounceFn = setTimeout(async () => {
            setGeoError('');

            // Strategy 1: Try Google Maps Geocoding
            if (window.google && window.google.maps) {
                try {
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: editForm.address }, async (results, status) => {
                        if (status === 'OK' && results && results[0]) {
                            const location = results[0].geometry.location;
                            setEditForm(prev => ({
                                ...prev,
                                latitude: location.lat(),
                                longitude: location.lng()
                            }));
                        } else {
                            // Google failed, try Fallback
                            console.warn(`Google Geocoding failed (${status}), trying OSM fallback...`);
                            await tryOsmGeocoding(editForm.address);
                        }
                    });
                    return;
                } catch (e) {
                    console.error("Google Geocoding error", e);
                }
            }
            // Strategy 2: Fallback to OSM
            await tryOsmGeocoding(editForm.address);
        }, 1500);

        return () => clearTimeout(delayDebounceFn);
    }, [editForm.address, isEditing, isMapLoaded]);

    // Detect User Location when starting Edit
    useEffect(() => {
        if (isEditing && isMapLoaded && (!editForm.latitude || !editForm.longitude)) {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;

                        // Only auto-fill coords if they are missing
                        setEditForm(prev => ({
                            ...prev,
                            latitude: prev.latitude || latitude,
                            longitude: prev.longitude || longitude
                        }));
                    },
                    (error) => console.warn("Geolocation access denied", error)
                );
            }
        }
    }, [isEditing, isMapLoaded]);

    const tryOsmGeocoding = async (address) => {
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
            const data = await response.json();

            if (data && data.length > 0) {
                setEditForm(prev => ({
                    ...prev,
                    latitude: parseFloat(data[0].lat),
                    longitude: parseFloat(data[0].lon)
                }));
            } else {
                setGeoError('Location not found.');
            }
        } catch (err) {
            console.error("OSM Geocoding error:", err);
            setGeoError('Could not find location.');
        }
    };

    const loadCompanyInfo = async () => {
        try {
            // Get tenant ID from auth service
            const tenantId = authService.getTenantId();

            if (tenantId) {
                // Fetch fresh data from API
                try {
                    const freshTenant = await authService.getTenant(tenantId);
                    if (freshTenant) {
                        setCompanyInfo(freshTenant);
                        setEditForm(freshTenant);

                        // Update local storage to keep it in sync
                        localStorage.setItem(
                          'tenant',
                          JSON.stringify(stripTenantModuleHints(freshTenant)),
                        );
                        return;
                    }
                } catch (apiError) {
                    console.warn("Failed to fetch fresh tenant info, falling back to local storage", apiError);
                }
            }

            // Fallback: Try to get from localStorage
            const savedTenant = localStorage.getItem('tenant');
            if (savedTenant && savedTenant !== 'undefined' && savedTenant !== 'null') {
                const info = JSON.parse(savedTenant);
                setCompanyInfo(info);
                setEditForm(info);
            } else {
                // Last resort: Get current user which might have tenant info
                const user = await authService.getCurrentUser();
                if (user && user.tenant) {
                    setCompanyInfo(user.tenant);
                    setEditForm(user.tenant);
                }
            }
        } catch (error) {
            console.error("Failed to load company info", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditToggle = () => {
        if (!canManageTenant) {
            setError('You do not have permission to edit tenant information');
            return;
        }
        setIsEditing(!isEditing);
        if (!isEditing) {
            setEditForm(companyInfo); // Reset form when starting edit
            setError(null);
            setSuccessMessage('');
            isTypingLocation.current = false;

            // Check if industry is custom
            const isCustom = companyInfo?.industry && !INDUSTRY_OPTIONS.includes(companyInfo.industry);
            setShowCustomIndustry(!!isCustom);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Validation for Contact Person (No numbers)
        if (name === 'contactPerson') {
            const letterOnlyValue = value.replace(/[^a-zA-Z\s]/g, '');
            setEditForm(prev => ({ ...prev, [name]: letterOnlyValue }));
            return;
        }

        // Validation for Contact Phone (Numbers only, Max 10 digits)
        if (name === 'phone') {
            const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
            setEditForm(prev => ({ ...prev, [name]: numericValue }));
            return;
        }

        if (name === 'address') {
            isTypingLocation.current = true;
            setGeoError('');
        }
        setEditForm(prev => {
            const updates = { ...prev, [name]: value };
            if (name === 'address' && !value) {
                // Clear coords if address is cleared
                updates.latitude = '';
                updates.longitude = '';
            }
            return updates;
        });
    };

    const handleMapClick = useCallback((event) => {
        if (!isEditing || !canManageTenant) return;
        isTypingLocation.current = false;
        setEditForm(prev => ({
            ...prev,
            latitude: event.latLng.lat(),
            longitude: event.latLng.lng()
        }));
    }, [isEditing, canManageTenant]);

    const handleMarkerDragEnd = useCallback((event) => {
        if (!isEditing || !canManageTenant) return;
        isTypingLocation.current = false;
        setEditForm(prev => ({
            ...prev,
            latitude: event.latLng.lat(),
            longitude: event.latLng.lng()
        }));
    }, [isEditing, canManageTenant]);

    const handleSave = async () => {
        if (!canManageTenant) {
            setError('You do not have permission to update tenant information');
            return;
        }

        // --- Validations ---
        if (!editForm.contactPerson || editForm.contactPerson.trim().length === 0) {
            setError('Contact person name is required');
            return;
        }

        // Email Validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!editForm.email || !emailRegex.test(editForm.email)) {
            setError('Please enter a valid email address (e.g., example@domain.com)');
            return;
        }

        // Phone Validation (Exactly 10 digits)
        if (!editForm.phone || editForm.phone.length !== 10) {
            setError('Contact phone must be exactly 10 digits');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            // Call API to update tenant
            // Pass the whole editForm which contains all fields
            const updatedTenant = await authService.updateTenant(companyInfo.tenantId, editForm);

            // Update state with new info
            // Ensure we merge properly if response is partial, but updateTenant usually handles local storage
            const mergedInfo = { ...companyInfo, ...editForm, ...updatedTenant };
            setCompanyInfo(mergedInfo);
            setIsEditing(false);
            setSuccessMessage('Company details updated successfully!');

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error("Failed to update company info", err);
            setError(err.response?.data?.message || err.message || "Failed to update company information");
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e) => {
        if (!canManageTenant) {
            setError('You do not have permission to upload tenant logo');
            return;
        }

        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Max 5MB
        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const response = await authService.uploadLogo(companyInfo.tenantId, file);
            setCompanyInfo(prev => ({ ...prev, logoUrl: response.logoUrl }));
            setSuccessMessage('Logo uploaded successfully');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error("Failed to upload logo", err);
            setError(err.response?.data?.message || err.message || "Failed to upload logo");
        } finally {
            setSaving(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    if (loading) {
        return <div className="company-page loading"><div className="spinner"></div></div>;
    }

    if (!canManageTenant) {
        return (
            <div className="company-page">
                <div className="card">
                    <h3>Access Denied</h3>
                    <p>You do not have permission to manage tenant information.</p>
                </div>
            </div>
        );
    }

    if (!companyInfo) {
        return (
            <div className="company-page">
                <div className="card">
                    <h3>Company Information</h3>
                    <p>No company information available. Please contact support.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="pagecontainer company-page">
            <div className="page-header">
                <div className='header-contain'>
                    <h1>Company Settings</h1>
                    <p>View and manage your organization's profile</p>
                </div>
                <div className="header-actions">
                    {!isEditing ? (
                        <button className="btn-secondary btn" onClick={handleEditToggle} disabled={!canManageTenant}>
                            <HiPencilSquare /> Edit Details
                        </button>
                    ) : (
                        <div className="action-buttons">
                            <button className="btn-secondary btn" onClick={handleEditToggle} disabled={saving || !canManageTenant}>
                                <HiXMark /> Cancel
                            </button>
                            <button className="btn-primary btn" onClick={handleSave} disabled={saving || !canManageTenant}>
                                {saving ? 'Saving...' : <><HiCheck /> Save Changes</>}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            <div className="company-details-container">
                <div className="card company-card">
                    <div className="company-header">
                        <div className="company-avatar">
                            {companyInfo.logoUrl ? (
                                <img src={companyInfo.logoUrl} alt="Company Logo" className="company-logo-img" />
                            ) : (
                                <HiOutlineBuildingOffice2 />
                            )}
                            {isEditing && canManageTenant && (
                                <div className="avatar-upload-overlay" onClick={() => fileInputRef.current?.click()}>
                                    <HiCamera />
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        className="hidden-file-input"
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="company-title">
                            <h3>{companyInfo.tenantName || 'Your Company'}</h3>
                            <span className="tenant-badge">Tenant Code: {companyInfo.tenantCode || 'N/A'}</span>
                        </div>
                    </div>

                    <div className="info-grid">
                        <div className="info-item">
                            <label><HiOutlineIdentification /> Account ID</label>
                            <div className="value read-only">{companyInfo.tenantId}</div>
                        </div>

                        <div className="info-item">
                            <label><HiOutlineUser /> Contact Person</label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    name="contactPerson"
                                    className="form-input"
                                    value={editForm.contactPerson || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter contact person name"
                                    disabled={!canManageTenant}
                                />
                            ) : (
                                <div className="value">{companyInfo.contactPerson || 'N/A'}</div>
                            )}
                        </div>

                        <div className="info-item">
                            <label><HiOutlineEnvelope /> Contact Email</label>
                            {isEditing ? (
                                <input
                                    type="email"
                                    name="email"
                                    className="form-input"
                                    value={editForm.email || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter contact email"
                                    disabled={!canManageTenant}
                                />
                            ) : (
                                <div className="value">{companyInfo.email || 'N/A'}</div>
                            )}
                        </div>

                        <div className="info-item">
                            <label><HiOutlinePhone /> Contact Phone</label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    name="phone"
                                    className="form-input"
                                    value={editForm.phone || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter contact phone (10 digits)"
                                    maxLength={10}
                                    disabled={!canManageTenant}
                                />
                            ) : (
                                <div className="value">{companyInfo.phone || 'N/A'}</div>
                            )}
                        </div>


                        <div className="info-item">
                            <label><HiOutlineBriefcase /> Industry</label>
                            {isEditing ? (
                                showCustomIndustry ? (
                                    <div className="custom-industry-input" style={{ display: 'flex', gap: '0.5rem' }}>
                                        <input
                                            type="text"
                                            name="industry"
                                            className="form-input"
                                            value={editForm.industry || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter industry"
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => setShowCustomIndustry(false)}
                                            title="Back to list"
                                            style={{ padding: '0.75rem' }}
                                        >
                                            <HiXMark />
                                        </button>
                                    </div>
                                ) : (
                                    <select
                                        name="industry"
                                        className="form-input"
                                        value={editForm.industry && INDUSTRY_OPTIONS.includes(editForm.industry) ? editForm.industry : (editForm.industry ? 'Other' : '')}
                                        onChange={(e) => {
                                            if (e.target.value === 'Other') {
                                                setShowCustomIndustry(true);
                                                // Keep the current value if it was already "Other" or custom? 
                                                // For now, if they explicitly click Other, we let them type.
                                                // Use setEditForm to clear it for typing? 
                                                // Actually, if we switch to custom, we might want to start fresh or keep previous if it was valid?
                                                // Let's clear it to allow typing fresh
                                                handleInputChange({ target: { name: 'industry', value: '' } });
                                            } else {
                                                handleInputChange(e);
                                            }
                                        }}
                                        disabled={!canManageTenant}
                                    >
                                        <option value="" disabled>Select Industry</option>
                                        {INDUSTRY_OPTIONS.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                        <option value="Other">Other (Specify)</option>
                                    </select>
                                )
                            ) : (
                                <div className="value">{companyInfo.industry || 'N/A'}</div>
                            )}
                        </div>

                        <div className="info-item full-width">
                            <label><HiOutlineMapPin /> Address</label>
                            {isEditing ? (
                                <div>
                                    <textarea
                                        name="address"
                                        className="form-input"
                                        value={editForm.address || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter company address"
                                        rows={3}
                                        disabled={!canManageTenant}
                                    />
                                    {geoError && (
                                        <span className="field-error" style={{ color: '#ef4444', fontSize: '0.85rem' }}>{geoError}</span>
                                    )}

                                    {/* Edit Mode Map */}
                                    <div className="map-wrapper" style={{ marginTop: '1rem' }}>
                                        {loadError ? (
                                            <div className="map-error" style={{ padding: '2rem', textAlign: 'center', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                                                <p style={{ fontWeight: 600 }}>Map cannot be loaded</p>
                                                <p style={{ fontSize: '0.85rem' }}>{loadError.message}</p>
                                                <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Please ensure a valid Google Maps API Key is added</p>
                                            </div>
                                        ) : isMapLoaded ? (
                                            <GoogleMap
                                                mapContainerStyle={mapContainerStyle}
                                                center={editForm.latitude && editForm.longitude
                                                    ? { lat: parseFloat(editForm.latitude), lng: parseFloat(editForm.longitude) }
                                                    : defaultCenter}
                                                zoom={12}
                                                onClick={canManageTenant ? handleMapClick : undefined}
                                            >
                                                {(editForm.latitude && editForm.longitude) && (
                                                    <Marker
                                                        position={{ lat: parseFloat(editForm.latitude), lng: parseFloat(editForm.longitude) }}
                                                        draggable={canManageTenant}
                                                        onDragEnd={canManageTenant ? handleMarkerDragEnd : undefined}
                                                    />
                                                )}
                                            </GoogleMap>
                                        ) : (
                                            <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                                                <div className="spinner"></div>
                                                <p>Loading Map...</p>
                                            </div>
                                        )}
                                        <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginTop: '0.5rem' }}>
                                            Click on the map to set location. Drag marker to refine. Latitude: {editForm.latitude || '-'}, Longitude: {editForm.longitude || '-'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="value" style={{ marginBottom: '1rem' }}>{companyInfo.address || 'N/A'}</div>

                                    {/* View Mode Map (Read Only) */}
                                    {companyInfo.latitude && companyInfo.longitude && isMapLoaded && (
                                        <div className="map-wrapper-readonly" style={{ pointerEvents: 'none' }}>
                                            <GoogleMap
                                                mapContainerStyle={{ ...mapContainerStyle, height: '200px' }}
                                                center={{ lat: parseFloat(companyInfo.latitude), lng: parseFloat(companyInfo.longitude) }}
                                                zoom={12}
                                                options={{ disableDefaultUI: true, draggable: false }}
                                            >
                                                <Marker position={{ lat: parseFloat(companyInfo.latitude), lng: parseFloat(companyInfo.longitude) }} />
                                            </GoogleMap>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="info-item">
                            <label>Subscription Plan</label>
                            <div className="value badge-primary">{companyInfo.subscriptionPlan || companyInfo.subscriptionPlanName || 'Standard'}</div>
                        </div>

                        <div className="info-item">
                            <label>Status</label>
                            <div className="value status-active">Active</div>
                        </div>

                        <div className="info-item">
                            <label>Created At</label>
                            <div className="value">{companyInfo.createdAt ? new Date(companyInfo.createdAt).toLocaleDateString() : 'N/A'}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Company;
